import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '@/core/contexts/AuthContext';
import { useInvalidateNotifications } from '@/hooks/queries/useNotificationQueries';

const WebSocketContext = createContext();

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

/**
 * WebSocket Provider for real-time updates
 * Replaces 30-second polling with instant updates
 */
export const WebSocketProvider = ({ children }) => {
  const { user, getUserInfoForApi } = useAuth();
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [lastMessage, setLastMessage] = useState(null);
  const [connectionError, setConnectionError] = useState(null);
  const invalidateNotifications = useInvalidateNotifications();
  
  const WS_URL = import.meta.env.VITE_WS_URL;
  
  const connect = useCallback(() => {
    // Check if we have a logged-in user OR registration session
    const hasLoggedInUser = user && getUserInfoForApi()?.user_id;
    
    if (!hasLoggedInUser) {
      return;
    }
    
    let userInfo;
    if (hasLoggedInUser) {
      // Use logged-in user info
      userInfo = getUserInfoForApi();
    }
    
    // Check if WebSocket URL is configured
    if (!WS_URL || WS_URL === 'undefined') {
      console.warn('🔌 [WebSocketContext] WebSocket URL not configured, real-time features disabled');
      setConnectionError('WebSocket URL not configured');
      return;
    }
    
    // Validate WebSocket URL format
    if (!WS_URL.startsWith('wss://') && !WS_URL.startsWith('ws://')) {
      console.error('🔌 [WebSocketContext] Invalid WebSocket URL format:', WS_URL);
      setConnectionError('Invalid WebSocket URL format');
      return;
    }
    

    
    // Don't attempt reconnection if we've exceeded max attempts
    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.warn(`🔌 [WebSocketContext] Max reconnection attempts (${maxReconnectAttempts}) exceeded`);
      setConnectionError(`Connection failed after ${maxReconnectAttempts} attempts`);
      return;
    }
    
    // Close existing connection if any
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      wsRef.current.close();
    }
    
    // Build WebSocket URL with user info as query parameters (required by backend)
    const params = new URLSearchParams({
      user_id: userInfo.user_id || userInfo.id || user.user_id || user.id,
      user_type: userInfo.user_type || userInfo.type || user.user_type || user.type || 'investor',
      user_name: userInfo.user_name || userInfo.name || user.user_name || user.name || 'Unknown User'  // Remove double encoding
    });
    
    const wsUrl = `${WS_URL}?${params.toString()}`;
    
    setConnectionStatus('connecting');
    setConnectionError(null);
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      // Connection timeout (10 seconds)
      const connectionTimeout = setTimeout(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING) {
          console.warn('🔌 [WebSocketContext] Connection timeout, closing...');
          wsRef.current.close();
          setConnectionError('Connection timeout');
        }
      }, 10000);
      
      wsRef.current.onopen = () => {
        clearTimeout(connectionTimeout);
        
        setConnectionStatus('connected');
        setConnectionError(null);
        reconnectAttemptsRef.current = 0; // Reset attempts on successful connection
        
        // Signal to messagingService that WebSocketContext is active
        window.webSocketContextActive = true;
        // Expose send function for messagingService coordination
        window.webSocketContextSend = (message) => {
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify(message));
            return true;
          }
          console.warn('⚠️ [WebSocketContext] WebSocket not connected - cannot send message');
          return false;
        };
        
        // Clear any reconnect timeout
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      };
      
      wsRef.current.onmessage = (event) => {
        // Parse and handle the message
        try {
          const message = JSON.parse(event.data);
          
          setLastMessage(message);
          
          // Get message type from either 'type' (notifications) or 'action' (ping/pong)
          const messageType = message.type || message.action;
          

          
          // Handle different message types
          switch (messageType) {
            case 'connection_established':
            case 'authentication_confirmed':
              console.log('�� [WebSocketContext] Connection established');
              break;
              
            case 'new_notification':
            case 'notification_read':
            case 'all_notifications_read':
            case 'notification':
              // Handle notification events
              invalidateNotifications(messageType, message.data);
              
              // Check if this notification contains a message_status_update event
              if (message.data?.event_type === 'message_status_update') {
                // Dispatch DOM event for message status updates
                document.dispatchEvent(new CustomEvent('websocket:message_status_update', {
                  detail: message.data.data
                }));
              }
              
              // Dispatch DOM event for useRealTimeNotifications
              document.dispatchEvent(new CustomEvent('websocket:notification', {
                detail: { event_type: messageType, ...message.data }
              }));
              break;
              
            case 'new_message':
              // Dispatch DOM event for messaging
              document.dispatchEvent(new CustomEvent('websocket:new_message', {
                detail: message.data
              }));
              break;
              
            case 'message_status_update':
              // Dispatch DOM event for message status updates
              document.dispatchEvent(new CustomEvent('websocket:message_status_update', {
                detail: message.data
              }));
              break;
              
            case 'message_read':
              // Dispatch DOM event for message read status updates
              document.dispatchEvent(new CustomEvent('websocket:message_read', {
                detail: message.data
              }));
              break;
              
            case 'watchlist_update':
              // Dispatch DOM event for watchlist updates
              document.dispatchEvent(new CustomEvent('websocket:watchlist_update', {
                detail: message.data
              }));
              break;
              
            case 'analytics_update':
              // Dispatch DOM event for analytics updates
              document.dispatchEvent(new CustomEvent('websocket:analytics_update', {
                detail: message.data
              }));
              break;

            case 'crm_update':
              // Dispatch DOM event for Sales CRM refresh (marketing sync + onboarding milestones)
              document.dispatchEvent(new CustomEvent('websocket:crm_update', {
                detail: message.data
              }));
              break;
              
            case 'user_status':
              // Dispatch DOM event for user status
              document.dispatchEvent(new CustomEvent('websocket:user_status', {
                detail: message.data
              }));
              break;
              
            case 'typing':
              // Dispatch DOM event for typing indicators
              document.dispatchEvent(new CustomEvent('websocket:typing', {
                detail: message.data
              }));
              break;
              
            case 'pong':
              // Handle ping/pong for keepalive (silent)
              break;
              
            case 'error':
              setConnectionError(message.message);
              break;
              
            default:
              // Unknown message type - only log if it's not ping/pong
              break;
          }
        } catch {
          setConnectionError('Failed to parse server message');
        }
      };
      
      wsRef.current.onclose = (event) => {
        clearTimeout(connectionTimeout);
        setConnectionStatus('disconnected');
        
        // Clear coordination flag and function reference
        window.webSocketContextActive = false;
        window.webSocketContextSend = null;
        
        // Handle different close codes and provide appropriate error messages
        let errorMessage = null;
        let shouldReconnect = false;
        
        switch (event.code) {
          case 1000: // Normal closure
            break;
            
          case 1001: // Going away
            break;
            
          case 1006: // Abnormal closure
            errorMessage = 'Connection failed - server may be unavailable';
            shouldReconnect = reconnectAttemptsRef.current < maxReconnectAttempts;
            break;
            
          case 403: // Forbidden
            errorMessage = 'Connection rejected - authentication failed';
            break;
            
          case 400: // Bad request
            errorMessage = 'Connection rejected - invalid request parameters';
            break;
            
          default:
            errorMessage = `Connection closed (${event.code})`;
            shouldReconnect = reconnectAttemptsRef.current < maxReconnectAttempts;
        }
        
        if (errorMessage) {
          setConnectionError(errorMessage);
        }
        
        // Automatic reconnection with exponential backoff
        if (shouldReconnect) {
          reconnectAttemptsRef.current++;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current - 1), 30000); // Max 30 seconds
          
          reconnectTimeoutRef.current = setTimeout(() => {
            if (user && userInfo?.user_id) { // Only reconnect if user is still logged in
              connect();
            }
          }, delay);
        }
      };
      
      wsRef.current.onerror = () => {
        setConnectionStatus('error');
        setConnectionError('WebSocket connection error');
      };
      
    } catch {
      setConnectionStatus('error');
      setConnectionError('Failed to create WebSocket connection');
    }
  }, [user, getUserInfoForApi, invalidateNotifications, WS_URL]);
  
  const disconnect = useCallback(() => {
    
    // Clear reconnection attempts and timeout
    reconnectAttemptsRef.current = 0;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    // Clear global flags
    window.webSocketContextActive = false;
    window.webSocketContextSend = null;
    
    if (wsRef.current) {
      // Set status before closing to prevent reconnection
      setConnectionStatus('disconnected');
      setConnectionError(null);
      
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }
  }, []);
  
  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify(message));
        return true;
      } catch {
        setConnectionError('Failed to send message');
        return false;
      }
    }
    return false;
  }, []);
  
  // Ping/pong for keepalive
  const sendPing = useCallback(() => {
    return sendMessage({
      action: 'message',
      type: 'ping',
      data: { timestamp: Date.now() }
    });
  }, [sendMessage]);
  
  // Manual reconnection function
  const reconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0; // Reset attempts for manual reconnection
    setConnectionError(null);
    disconnect();
    setTimeout(() => {
      if (user) {
        connect();
      }
    }, 1000);
  }, [user, connect, disconnect]);
  
  // Connect when user logs in
  useEffect(() => {
    if (user) {
      connect();
    } else {
      disconnect();
    }
    
    return () => {
      disconnect();
    };
  }, [user, connect, disconnect]);
  
  // Setup ping interval for keepalive
  useEffect(() => {
    if (connectionStatus === 'connected') {
      const pingInterval = setInterval(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          sendPing();
        }
      }, 30000); // Ping every 30 seconds
      
      return () => clearInterval(pingInterval);
    }
  }, [connectionStatus, sendPing]);
  
  const value = useMemo(() => ({
    connectionStatus,
    connectionError,
    lastMessage,
    sendMessage,
    sendPing,
    connect,
    disconnect,
    reconnect,
    // Registration-specific connection method
    // Additional debugging info
    reconnectAttempts: reconnectAttemptsRef.current,
    maxReconnectAttempts
  }), [connectionStatus, connectionError, lastMessage, sendMessage, sendPing, connect, disconnect, reconnect]);
  
  return (
    <WebSocketContext.Provider value={value} data-websocket-context="true">
      {children}
    </WebSocketContext.Provider>
  );
};

WebSocketProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default WebSocketContext; 
